import { useState, useCallback } from "react";

/**
 * useSidebarModals - Base hook for sidebar modal state management
 *
 * Shared state and logic for Collection and Simplify sidebar interactions.
 * Extensible for additional modal state via the extending hooks.
 */
export function useSidebarModals() {
  // Game preview
  const [previewGame, setPreviewGame] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Marketplace and reminder (rendered when set, no separate flag)
  const [marketplaceGame, setMarketplaceGame] = useState(null);
  const [reminderGame, setReminderGame] = useState(null);

  // Curate sidebar
  const [showCurateSidebar, setShowCurateSidebar] = useState(false);

  // Banners
  const [showGameSelectionBanner, setShowGameSelectionBanner] = useState(false);
  const [bannerMessage, setBannerMessage] = useState("");
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [successBannerMessage, setSuccessBannerMessage] = useState("");

  // Add game modal shared state structure
  const createInitialAddGameData = useCallback((game = null) => ({
    game_id: game ? (game.game_id || game.id) : null,
    name: game ? game.name : "",
    thumbnail: game ? game.thumbnail : "",
    preference: null,
    ownership_status: "no_shelf",
    has_played: false,
    ...(game?.isShared !== undefined && { isShared: game.isShared }),
    ...(game?.additional_tags && { additional_tags: game.additional_tags }),
  }), []);

  const [addGameData, setAddGameData] = useState(() => createInitialAddGameData());
  const [showAddGameModal, setShowAddGameModal] = useState(false);

  const openAddGameModal = useCallback((game = null) => {
    setAddGameData(createInitialAddGameData(game));
    setShowAddGameModal(true);
  }, [createInitialAddGameData]);

  const closeAddGameModal = useCallback(() => {
    setShowAddGameModal(false);
    setAddGameData(createInitialAddGameData());
  }, [createInitialAddGameData]);

  // Banner helpers
  const showTransientBanner = useCallback((message) => {
    setBannerMessage(message);
    setShowGameSelectionBanner(true);
    setTimeout(() => setShowGameSelectionBanner(false), 3000);
  }, []);

  const showSuccessBannerFunc = useCallback((message) => {
    setSuccessBannerMessage(message);
    setShowSuccessBanner(true);
    setTimeout(() => setShowSuccessBanner(false), 3000);
  }, []);

  return {
    // Preview
    previewGame,
    setPreviewGame,
    loadingPreview,
    setLoadingPreview,

    // Marketplace/Reminder
    marketplaceGame,
    setMarketplaceGame,
    reminderGame,
    setReminderGame,

    // Curate sidebar
    showCurateSidebar,
    setShowCurateSidebar,

    // Banners
    showGameSelectionBanner,
    setShowGameSelectionBanner,
    bannerMessage,
    setBannerMessage,
    showSuccessBanner,
    setShowSuccessBanner,
    successBannerMessage,
    setSuccessBannerMessage,
    showTransientBanner,
    showSuccessBannerMessage: showSuccessBannerFunc,

    // Add game modal
    addGameData,
    setAddGameData,
    showAddGameModal,
    setShowAddGameModal,
    openAddGameModal,
    closeAddGameModal,
    createInitialAddGameData,
  };
}
